/**
 * Fait : l'écran « Mes projets » — la liste de ce qui m'est partagé, avec le
 *   NIVEAU de chacun affiché, et la création d'un projet. C'est la seconde
 *   voie du critère 14 : ce que l'interface montre et permet découle du
 *   niveau rendu par l'API, jamais d'une règle réécrite ici.
 *
 * Ne fait pas : elle ne devine aucun droit — un bouton absent l'est parce que
 *   l'API a dit le niveau — et ne parle pas au canal : la présence vit dans
 *   la fiche d'un projet, pas dans la liste.
 */

import { useEffect, useState } from 'react';

import type { NiveauDeMembre } from '@aymoai/shared';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import { ChoixDuReferent } from './ChoixDuReferent.tsx';
import { type ProfilVu, type ProjetVu, type ReferentPossibleVu } from './api.ts';
import { creeUnProjet, listeLesProjets, listeLesReferentsPossibles } from './api-projet.ts';

/** Ce que le niveau donne le droit de faire, dit en français. */
const CE_QUE_LE_NIVEAU_PERMET: Readonly<Record<ProjetVu['niveau'], string>> = {
  proprietaire: 'Vous administrez ce projet',
  editeur: 'Vous pouvez y écrire',
  lecteur: 'Vous le lisez',
};

/**
 * L'aide du champ de nom, sortie du JSX pour tenir dans la largeur du code.
 *
 * Elle porte le renvoi exigé par `D-033` : le produit ne promet plus le secret
 * tout court, il promet le secret DANS Clannik, et dit où lire le reste.
 */
const AIDE_DU_NOM =
  'Il sera visible par les membres que vous inviterez, et par personne d’autre dans ' +
  'Clannik — voir « Ce que Clannik voit ».';

export function PageProjets({ profil }: { readonly profil: ProfilVu }) {
  const [projets, setProjets] = useState<ProjetVu[] | null>(null);
  const [echec, setEchec] = useState<string | null>(null);
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [enCours, setEnCours] = useState(false);

  /*
   * LE RÉFÉRENT (`D-023`). Un membre doit en désigner un ; un parent
   * administrateur n'a personne à désigner, et pour lui ces trois états ne
   * servent à rien. C'est le SERVEUR qui tranche l'obligation — lui seul
   * connaît le rôle — l'écran ne fait que poser le champ à qui le concerne.
   */
  const doitChoisirUnReferent = profil.role === 'member';
  const [referents, setReferents] = useState<ReferentPossibleVu[] | null>(null);
  const [referentChoisi, setReferentChoisi] = useState('');
  const [niveauDuReferent, setNiveauDuReferent] = useState<NiveauDeMembre>('lecture');

  /** Recharge la liste — appelée au montage et après chaque création. */
  async function recharge(): Promise<void> {
    const issue = await listeLesProjets();

    if (issue.bon) {
      setProjets(issue.valeur);
      setEchec(null);
      return;
    }

    setEchec(issue.erreur.message);
  }

  useEffect(() => {
    void recharge();
  }, []);

  useEffect(() => {
    if (!doitChoisirUnReferent) return;

    void (async () => {
      const issue = await listeLesReferentsPossibles();
      if (!issue.bon) {
        setEchec(issue.erreur.message);
        return;
      }

      setReferents(issue.valeur);

      // Un seul candidat : il est CHOISI, pas seulement affiché. Laisser
      // l'entrée vide sélectionnée obligerait à « choisir » ce qui n'offre
      // aucun choix, et le bouton refuserait sans qu'on comprenne pourquoi.
      const seul = issue.valeur.length === 1 ? issue.valeur[0] : undefined;
      if (seul !== undefined) setReferentChoisi(seul.compteId);
    })();
  }, [doitChoisirUnReferent]);

  /** Crée le projet saisi, puis recharge. */
  async function cree(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    if (nom.trim() === '') return;

    setEnCours(true);
    const issue = await creeUnProjet(
      nom.trim(),
      description.trim(),
      doitChoisirUnReferent && referentChoisi !== ''
        ? { compteId: referentChoisi, niveau: niveauDuReferent }
        : undefined,
    );
    setEnCours(false);

    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    setNom('');
    setDescription('');
    await recharge();
  }

  if (echec !== null) {
    return (
      <section data-etat="erreur">
        <h2>Vos projets n’ont pas pu être chargés</h2>
        <Annonce urgence="urgent">{echec}</Annonce>
        <button type="button" onClick={() => void recharge()}>
          Réessayer
        </button>
      </section>
    );
  }

  if (projets === null) {
    return (
      <section data-etat="chargement" aria-busy="true">
        <h2>Chargement de vos projets…</h2>
      </section>
    );
  }

  return (
    <div className="empilement" data-etat={projets.length === 0 ? 'vide' : 'nominal'}>
      <Partie
        etiquette="Ce qui vous est partagé"
        nature="projet"
        titre={projets.length === 0 ? 'Aucun projet pour l’instant' : 'Vos projets'}
        niveauDeTitre={2}
        aide="Un projet est un espace partagé : on y travaille à plusieurs, en même temps."
        data-role="partie-projets"
      >
        {projets.length > 0 && (
          <ul className="rangees" data-liste="projets">
            {projets.map((projet) => (
              <li key={projet.id} data-projet={projet.id} data-niveau={projet.niveau}>
                <a href={`#/projets/${projet.id}`}>{projet.nom}</a>
                <span className="texte-attenue">— {CE_QUE_LE_NIVEAU_PERMET[projet.niveau]}</span>
                {projet.archive ? <span className="texte-attenue">(archivé)</span> : null}
                {/*
                 * La description sous le nom : on doit savoir OÙ L'ON ENTRE
                 * avant d'entrer. C'est toute la raison de ce champ.
                 */}
                {projet.description !== undefined && (
                  <p className="description-de-projet" data-role="description">
                    {projet.description}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Partie>

      <Partie
        etiquette="Commencer quelque chose"
        nature="travail"
        titre="Créer un projet"
        aide={AIDE_DU_NOM}
        data-role="partie-creation"
      >
        <form onSubmit={(evenement) => void cree(evenement)}>
          <Champ
            libelle="Nom du nouveau projet"
            aide="Par exemple : « Vacances d’été » ou « Devoirs de Léa »."
            name="nom-du-projet"
            value={nom}
            onChange={(evenement) => setNom(evenement.target.value)}
          />
          <Champ
            libelle="Description (facultative)"
            aide="De quoi il retourne, en une ou deux phrases. Par exemple : « préparation du Noël 2027 »."
            name="description-du-projet"
            value={description}
            onChange={(evenement) => setDescription(evenement.target.value)}
          />

          {doitChoisirUnReferent && (
            <ChoixDuReferent
              candidats={referents}
              choisi={referentChoisi}
              surChoix={setReferentChoisi}
              niveau={niveauDuReferent}
              surNiveau={setNiveauDuReferent}
            />
          )}

          <button
            type="submit"
            data-role="principal"
            disabled={
              enCours || nom.trim() === '' || (doitChoisirUnReferent && referentChoisi === '')
            }
          >
            {enCours ? 'Création…' : 'Créer le projet'}
          </button>
        </form>
      </Partie>
    </div>
  );
}
